import numeral from 'numeral';
import useMoney from '@resources/js/app/composables/useMoney.js';
import useMortgage from '@resources/js/app/composables/useMortgage.js';

const { formatCurrencyConverted, formatMoney } = useMoney();

const { getMonthlyInterest, getMonthlyInstallments, getLoanTermMonths, getLoanX, getAdvancePayment, getTotalInterest } =
    useMortgage();

export default (
    $wire,
    $watch,
    $dispatch,
    {
        entityPrice,
        primaryCurrency,
        defaultLoanAmount,
        maxMortgageAmount,
        defaultLoanTenure,
        defaultLoanInterest,
        defaultLoanAdvance = 0,
        hasDonutGraph = false,
        currencySeparator = ',',
        secondaryCurrency = null,
        currencyExchangeRate = null,
    },
) => ({
    numeral: null,
    loading: false,
    loanInterest: null,
    defaultMaxLoanAmount: null,
    exposeForm: $wire.entangle('exposeForm').live,
    loanAmount: $wire.entangle('loanAmount'),
    loanTenure: $wire.entangle('loanTenure'),
    loanAdvance: $wire.entangle('loanAdvance'),
    sCampaignIds: $wire.entangle('selectedCampaignIds').live,
    selectedCampaignIds: $wire.entangle('selectedCampaignIds').live,
    enquirySubmittedSuccessfully: $wire.entangle('enquirySubmittedSuccessfully').live,

    init: function () {
        this.numeral = numeral;
        this.loanAmount = defaultLoanAmount;
        this.loanTenure = defaultLoanTenure;
        this.loanAdvance = defaultLoanAdvance;
        this.loanInterest = defaultLoanInterest;
        this.defaultMaxLoanAmount = maxMortgageAmount;

        Alpine.store('loanAmount', defaultLoanAmount);
        Alpine.store('loanTenure', defaultLoanTenure);
        Alpine.store('loanAdvance', defaultLoanAdvance);
        Alpine.store('creditAmount', this.creditAmount);
        Alpine.store('selectedMortgageCampaigns', {
            values: this.selectedCampaignIds,
        });

        $watch('loanAmount', value => Alpine.store('loanAmount', value));
        $watch('loanTenure', value => Alpine.store('loanTenure', value));
        $watch('creditAmount', value => Alpine.store('creditAmount', value));
        $watch('loanAdvance', value => this.loanAdvanceUpdated(value));

        Alpine.effect(() => {
            const selection = Alpine.store('selectedMortgageCampaigns');
            this.setSelectedCampaignIds(selection.values);
        });

        window.addEventListener('newValueAddedManually', event => {
            [model, newValue] = event.detail;
            this.newValueAddedManually(model, newValue);
        });
    },

    get maxLoanAmount() {
        return this.defaultMaxLoanAmount;
    },

    get formExposed() {
        return this.exposeForm && !this.enquirySubmittedSuccessfully;
    },

    get showFormToggle() {
        return !this.exposeForm && !this.enquirySubmittedSuccessfully;
    },

    get shouldCalculate() {
        return (
            !isNaN(this.monthly) &&
            this.monthly !== Number.POSITIVE_INFINITY &&
            this.monthly !== Number.NEGATIVE_INFINITY
        );
    },

    get repayment() {
        return getLoanTermMonths(this.loanTenure);
    },

    get interest() {
        return getMonthlyInterest(this.loanInterest);
    },

    get loanx() {
        return getLoanX(this.interest, this.repayment);
    },

    get monthly() {
        return getMonthlyInstallments(this.creditAmount, this.loanx, this.interest);
    },

    get creditAmount() {
        return this.loanAmount - this.advancePayment;
    },

    get advancePayment() {
        return getAdvancePayment(this.loanAdvance, this.loanAmount);
    },

    get formattedAdvancePayment() {
        return formatMoney(this.advancePayment, currencySeparator, primaryCurrency);
    },

    get monthlyRepayment() {
        if (this.shouldCalculate) {
            if (hasDonutGraph) {
                this.updateDonutColor();
            }
            return Math.round(this.monthly);
        }
    },

    get totalRepayment() {
        if (this.shouldCalculate) {
            return Math.round(this.monthly * this.repayment);
        }
    },

    get totalInterest() {
        if (this.shouldCalculate) {
            return getTotalInterest(this.monthly, this.repayment, this.creditAmount);
        }
    },

    get percentageDifference() {
        return Math.round(((this.totalRepayment - this.totalInterest) / this.totalRepayment) * 100);
    },

    get formattedLoanAmount() {
        return this.numeral(this.creditAmount).format('0.00 a');
    },

    get formattedCreditAmount() {
        return formatMoney(this.creditAmount, currencySeparator, primaryCurrency);
    },

    get formattedEntityPrice() {
        return formatMoney(entityPrice, currencySeparator, primaryCurrency);
    },

    get formattedCreditAmountConverted() {
        return formatCurrencyConverted(
            this.creditAmount,
            currencySeparator,
            primaryCurrency,
            secondaryCurrency,
            currencyExchangeRate,
        );
    },

    updateDonutColor() {
        var perc = this.percentageDifference,
            color2 = '#006FBC',
            base2 = '#9CA3AF',
            deg = (perc / 100) * 360,
            deg1 = 90,
            deg2 = deg;

        // If percentage is less than 50%
        if (perc < 50) {
            var base = '#006FBC';
            var color = base2;
            (color2 = base2), (deg1 = (perc / 100) * 360 + 90);
            deg2 = 0;
        }

        document.querySelector('.chart1').style.backgroundColor = base;

        var elem = document.querySelector('.slice.one');
        elem.style.transform = `rotate(${deg1}deg)`;
        elem.style.transitionDuration = '2s';
        elem.style.transitionDelay = '1s';
        elem.style.backgroundColor = color;

        var elem2 = document.querySelector('.slice.two');
        elem2.style.transform = `rotate(${deg2}deg)`;
        elem2.style.transitionDuration = '2s';
        elem2.style.transitionDelay = '1s';
        elem2.style.backgroundColor = color2;
    },

    displayFormat(value) {
        return this.numeral(value).format('0,00');
    },

    inputLabel(value, isPrice = null, currency = null, currencyPosition = 'prefix') {
        if (isPrice === true) {
            let format = '0,0';

            if (currency !== null) {
                if (currencyPosition === 'prefix') {
                    format = currency + ' ' + format;
                } else {
                    format = format + ' ' + currency;
                }
            }

            this.numeral(value).format(format);
        }

        return value;
    },

    setSelectedCampaignIds(ids) {
        this.selectedCampaignIds = ids;
    },

    getModel(inputName) {
        const inputModel = {
            amount: 'loanAmount',
            advance: 'loanAdvance',
            period: 'loanTenure',
        };

        return inputModel[inputName];
    },

    setModelValue(inputName, value) {
        this[inputName] = value;
    },

    getModelValue(inputName) {
        return this[inputName];
    },

    submitForm(submitAction) {
        this.loading = true;

        $wire.call(submitAction);
    },

    openForm(showModal = false) {
        this.showForm();

        if (showModal) {
            $dispatch('toggle-mortgage-calculator-lead-form', 'open');
        }
    },

    showForm() {
        this.exposeForm = true;
    },

    closeForm(showModal = false) {
        this.exposeForm = false;

        if (showModal) {
            $dispatch('toggle-mortgage-calculator-lead-form', 'close');
        }
    },

    newValueAddedManually(model, newValue) {
        this.setModelValue(model, newValue);

        $dispatch('range-input:set-value', {
            value: newValue,
            for: model,
        });
    },

    loanAdvanceUpdated(value) {
        Alpine.store('loanAdvance', value);

        $dispatch('custom-select:set-value', {
            value: value,
            for: 'loanAdvance',
        });
    },
});
